import type { TConversationsResponse } from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { AnswerEditorHandle } from '../../AnswerEditor';
import {
  buildDraftFromCheckResult,
  computeHasUnsavedChanges,
} from '../conversation-review.helpers';
import type { TDraftState } from '../conversation-review-modal.type';

type TUseConversationDraftStateParams = {
  initialData: TConversationsResponse;
  answerEditorRef: RefObject<AnswerEditorHandle | null>;
  onConversationIndexChange?: () => void;
};

export const useConversationDraftState = ({
  initialData,
  answerEditorRef,
  onConversationIndexChange,
}: TUseConversationDraftStateParams) => {
  const [conversations, setConversations] =
    useState<TConversationsResponse>(initialData);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [draftStates, setDraftStates] = useState<Record<number, TDraftState>>(
    {},
  );

  const currentConversation = useMemo(
    () => conversations.results[currentIndex],
    [conversations.results, currentIndex],
  );
  const totalConversations = conversations.results.length;

  // If no draft entry exists (user never touched this card, or changes were discarded),
  // fall back to a draft built from the persisted checkResult so the UI reflects saved state.
  const currentDraft = useMemo(() => {
    const existingDraft = draftStates[currentIndex];
    if (existingDraft) {
      return existingDraft;
    }
    return buildDraftFromCheckResult(currentConversation?.checkResult);
  }, [draftStates, currentIndex, currentConversation?.checkResult]);

  // True when the current card has pending changes that would be lost on navigation.
  // Uses the raw draftStates entry so we can distinguish "user never touched this card"
  // (no draft entry) from "draft exists but matches what is already saved".
  const hasUnsavedChanges = useMemo(
    () =>
      computeHasUnsavedChanges(
        draftStates[currentIndex],
        currentConversation?.checkResult,
      ),
    [draftStates, currentIndex, currentConversation?.checkResult],
  );

  // Latest conversations mirror so setCurrentDraft can read checkResult without taking
  // conversations as a dep (which would re-create every handler on every save).
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  const setCurrentDraft = useCallback(
    (updater: (prev: TDraftState) => TDraftState) => {
      setDraftStates((prev) => {
        const existingDraft = prev[currentIndex];
        if (existingDraft) {
          return { ...prev, [currentIndex]: updater(existingDraft) };
        }
        // Seed the draft from the persisted checkResult so existing values are preserved
        // when the user changes just one field on a previously-reviewed card.
        const conversation = conversationsRef.current.results[currentIndex];
        const seededDraft = buildDraftFromCheckResult(
          conversation?.checkResult,
        );
        return { ...prev, [currentIndex]: updater(seededDraft) };
      });
    },
    [currentIndex],
  );

  // Mirror the latest draftStates into a ref so the navigation-sync effect below
  // can read it without subscribing to it (which would re-fire on every keystroke
  // and reset the CKEditor's data, wiping the cursor/selection - that broke the
  // toolbar link button, which then inserted URLs at position 0).
  const draftStatesRef = useRef<Record<number, TDraftState>>({});
  draftStatesRef.current = draftStates;

  // Sync the answer editor only when the user navigates to another conversation.
  // The ref read above gives us the latest draft without re-firing on draft mutations.
  useEffect(() => {
    const draft = draftStatesRef.current[currentIndex];
    if (draft?.correctedAnswer && answerEditorRef.current) {
      answerEditorRef.current.setEditorText(draft.correctedAnswer);
    }
    onConversationIndexChange?.();
  }, [currentIndex, answerEditorRef, onConversationIndexChange]);

  // Drop the current draft entry so no pending changes persist (used by the
  // unsaved-changes prompt when the user chooses Discard).
  const discardCurrentDraft = useCallback(() => {
    setDraftStates((prev) => {
      const updated = { ...prev };
      delete updated[currentIndex];
      return updated;
    });
  }, [currentIndex]);

  // Remove the conversation at the current index from the list and shift draft
  // entries down to match the new indices. Steps the current index back when the
  // removed card was the last one so we never point out of bounds.
  const removeCurrentConversation = useCallback(() => {
    const removedIndex = currentIndex;
    const newLength = conversationsRef.current.results.length - 1;

    setConversations((prev) => ({
      ...prev,
      results: prev.results.filter(
        (_, resultIndex) => resultIndex !== removedIndex,
      ),
      totalRecords: newLength,
    }));

    setDraftStates((prev) => {
      const shifted: Record<number, TDraftState> = {};
      for (const [key, value] of Object.entries(prev)) {
        const draftIndex = Number(key);
        if (draftIndex < removedIndex) {
          shifted[draftIndex] = value;
        } else if (draftIndex > removedIndex) {
          shifted[draftIndex - 1] = value;
        }
      }
      return shifted;
    });

    if (newLength > 0 && removedIndex >= newLength) {
      setCurrentIndex(newLength - 1);
    }
  }, [currentIndex]);

  // Clear the pendingHotTopicId on the current draft (the no-op reassign case where
  // the user re-selected the current topic).
  const clearPendingHotTopic = useCallback(() => {
    setDraftStates((prev) => {
      const existing = prev[currentIndex];
      if (!existing) {
        return prev;
      }
      return {
        ...prev,
        [currentIndex]: { ...existing, pendingHotTopicId: undefined },
      };
    });
  }, [currentIndex]);

  return {
    conversations,
    setConversations,
    currentIndex,
    setCurrentIndex,
    totalConversations,
    currentConversation,
    draftStates,
    currentDraft,
    setCurrentDraft,
    hasUnsavedChanges,
    discardCurrentDraft,
    removeCurrentConversation,
    clearPendingHotTopic,
  };
};
